<template>
  <div class="trip-log-attachments-view" data-cy="trip-log-attachments-view">
    <div
      v-if="existingAttachments.length"
      class="trip-log-attachments-view__container"
    >
      <div
        class="trip-log-attachments-view__header"
        data-cy="ingredient-attachments-header"
      >
        <h3>Uploaded Attachments</h3>
      </div>
      <div class="trip-log-attachments-view__attachments">
        <div
          v-for="attachment in existingAttachments"
          :key="attachment.Name"
          class="trip-log-attachments-view__attachment"
          data-cy="ingredient-attachment"
        >
          <LoadingButton
            :loading="downloadingAttachment === attachment"
            type="button"
            theme-color="primary"
            @click="downloadAttachmentAsync(attachment)"
            data-cy="download-attachment-button"
            :disabled="
              downloadingAttachment != null &&
              downloadingAttachment !== attachment
            "
          >
            {{ attachment.Name }}{{ attachment.DocType }}
          </LoadingButton>
          <div
            class="trip-log-attachments-view__attachment__date"
            data-cy="attachment-date"
          >
            {{
              attachment.Modified &&
              new Date(attachment.Modified).toLocaleString()
            }}
          </div>
          <div class="trip-log-attachments-view__attachment__actions">
            <Button
              v-if="attachment.AttachmentType === 'Saved'"
              type="button"
              theme-color="error"
              @click="deleteAttachmentAsync(attachment)"
              data-cy="delete-attachment-button"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { onBeforeMount, ref } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import type { EirSharePointAttachment } from "@/Domain/EirSharePointAttachment";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useNotificationStore } from "@/stores/useNotificationStore";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import type { Attachment } from "@/Domain/Attachment";

const notificationStore = useNotificationStore();

interface FormAttachement {
  Name: string;
  FileUrl: string;
  DocType: string;
  Data: number[];
  Modified: string;
  AttachmentType: "Submitted" | "Saved";
}

const props = defineProps<{
  tripId: number;
  newTrip: boolean;
}>();

const existingAttachments = ref<FormAttachement[]>([]);
const downloadingAttachment = ref<FormAttachement | null>(null);

const updateFormAttachmentsAsync = async function () {
  if (!props.newTrip)
    return handleResponsePromiseAsync(
      api.get("GetEfrUploadedDocs", {
        tripId: props.tripId,
      }),
      (data) => {
        const attachments = data.map((attachment: EirSharePointAttachment) => ({
          Name: attachment.Title,
          FileUrl: attachment.FileUrl,
          DocType: "",
          Data: [] as number[],
          Modified: attachment.Modified,
          AttachmentType: "Submitted",
        })) as FormAttachement[];
        existingAttachments.value = [
          ...existingAttachments.value,
          ...attachments,
        ];
      },
      "TripLogAttachmentsView.vue",
      "updateFormAttachmentsAsync"
    );
  else
    return handleResponsePromiseAsync(
      api.get("GetSavedTripUploadedDocs", {
        tripId: props.tripId,
      }),
      (data) => {
        const attachments = data.map((attachment: Attachment) => ({
          Name: attachment.Name,
          FileUrl: attachment.FullPathName,
          DocType: attachment.DocType,
          Data: attachment.Data,
          Modified: attachment.Created?.toString() ?? "",
          AttachmentType: "Saved",
        })) as FormAttachement[];
        existingAttachments.value = [
          ...existingAttachments.value,
          ...attachments,
        ];
      },
      "TripLogAttachmentsView.vue",
      "updateFormAttachmentsAsync"
    );
};

const downloadAttachmentAsync = async function (attachment: FormAttachement) {
  downloadingAttachment.value = attachment;
  try {
    if (attachment.AttachmentType === "Saved") {
      const response = await fetch(
        `data:${attachment.DocType};base64,${attachment.Data}`
      );
      const blob = await response.blob();

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = attachment.Name + attachment.DocType;
      link.click();
      URL.revokeObjectURL(link.href);
    } else {
      const downloadPromise = api.getDownload("DownloadEfrDoc", {
        fileUrl: attachment.FileUrl,
        fileName: attachment.Name,
      });

      await handleResponsePromiseAsync(
        downloadPromise,
        () => {},
        "TripLogAttachmentsView.vue",
        "downloadAttachmentAsync",
        () => {
          notificationStore.notify({
            type: NotificationTypeEnum.error,
            message: `Error downloading ${attachment.Name}`,
            duration: 5000,
          });
        }
      );
    }
  } catch (error) {
    notificationStore.notify({
      type: NotificationTypeEnum.error,
      message: `Error downloading ${attachment.Name}`,
      duration: 5000,
    });
  }
  downloadingAttachment.value = null;
};

const deleteAttachmentAsync = async function (attachment: FormAttachement) {
  await handleResponsePromiseAsync(
    api.post("DeleteSavedTripFile", {
      TripId: props.tripId,
      FileName: attachment.Name + attachment.DocType,
    }),
    (data) => {
      if (data)
        existingAttachments.value.splice(
          existingAttachments.value.indexOf(attachment),
          1
        );
    },
    "TripLogAttachmentsView.vue",
    "deleteAttachmentAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error deleting ${attachment.Name}`,
        duration: 5000,
      });
    }
  );
};

defineExpose({
  existingAttachments,
});

onBeforeMount(async () => {
  await updateFormAttachmentsAsync();
});
</script>

<style lang="scss">
.trip-log-attachments-view {
  display: flex;
  flex-direction: column;
  width: 100%;

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__attachments {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow-y: auto;
  }

  &__attachment {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.5rem 1rem;
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    flex-wrap: wrap;
    gap: 4px;

    &__download {
      flex-grow: 3;
    }

    &__date {
      font-weight: bold;
      text-align: center;
      flex-basis: 190px;
    }

    &__actions {
      flex-basis: 60px;
      display: flex;
      justify-content: flex-end;
    }
  }
}
</style>
